// M004-F0646 — one card per assigned plan (design §3.2) and the locked base
// card. Chips (rooms, days, time) come from the SERVER scope summary
// (assignments read A7: plan.zones / plan.room / plan.rate_summary).
import React, { useState } from 'react';
import {
  DAY_INITIALS, DAY_LABELS, PATTERN_CSS, daysBetweenIso, formatPerHour, formatMinor,
  formatShortDate, scopeSummary, styleForPlan,
} from './planModel';

const Swatch = ({ style }) => (
  <span
    className="mpp-swatch"
    aria-hidden="true"
    style={{ backgroundColor: style.color, backgroundImage: style.pattern === 'solid' ? undefined : PATTERN_CSS[style.pattern] }}
  />
);

export const ScopeChips = ({ roomLabels, plan, style, onRemoveRoom }) => {
  const scope = scopeSummary(plan);
  return (
    <div className="mpp-chips" style={{ '--mpp-c': style.color }}>
      {roomLabels.map((room) => (
        <span className="mpp-chip" key={room.key} data-testid="plan-room-chip">
          {room.label}
          {onRemoveRoom && roomLabels.length > 1 ? (
            <button
              type="button"
              className="mpp-chip-x"
              aria-label={`Remove ${room.label}`}
              onClick={() => onRemoveRoom(room)}
            >
              &times;
            </button>
          ) : null}
        </span>
      ))}
      {DAY_LABELS.map((label, index) => (
        <span
          key={label}
          className={`mpp-chip mpp-chip-day${scope.days[index] ? ' on' : ''}`}
          title={label}
          data-testid="plan-day-chip"
          data-day={label}
          data-on={scope.days[index] ? 'true' : 'false'}
        >
          {DAY_INITIALS[index]}
        </span>
      ))}
      {scope.times.map((time) => (
        <span className="mpp-chip mpp-chip-time" key={time} data-testid="plan-time-chip">{time}</span>
      ))}
    </div>
  );
};

export const BaseCard = ({ name = 'Studio rates (everyone)', rateMinor, baseStyle, roomName }) => (
  <div className="mpp-card mpp-card-locked" style={{ '--mpp-c': baseStyle.color }} data-testid="price-plan-base-card">
    <div className="mpp-card-top">
      <Swatch style={baseStyle} />
      <span className="mpp-card-name">{name}</span>
      {Number.isFinite(rateMinor) ? <span className="mpp-rate" data-testid="price-plan-base-rate">{formatPerHour(rateMinor)}</span> : null}
      <span title="Room default - always applies" role="img" aria-label="Locked: room default always applies">{'🔒'}</span>
    </div>
    <div className="mpp-chips">
      <span className="mpp-chip">{roomName || 'Selected room'}</span>
      {DAY_LABELS.map((label, index) => (
        <span key={label} className="mpp-chip mpp-chip-day on" style={{ '--mpp-c': baseStyle.color }}>{DAY_INITIALS[index]}</span>
      ))}
      <span className="mpp-chip mpp-chip-time">Any time</span>
    </div>
    <div className="mpp-cardfoot mpp-muted">Room default. Always a candidate; set on each room, not per person.</div>
  </div>
);

const PlanCard = ({
  group, styles, today, canEdit, isNew = false, pendingRemoval = false, dateValue, dirtyDate = false,
  noRoomDefaultRoomIds = [], onRemove, onUndo, onDateChange, onRemoveRoom,
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const style = styleForPlan(styles, group.planIds?.[0] ?? group.planId);
  const rate = group.rateSummary;
  const roomLabels = group.rooms.map((room) => ({ key: `${room.assignmentId || room.planId}`, label: room.roomName, ...room }));
  const effectiveTo = group.effectiveTo;
  const endsInDays = effectiveTo ? daysBetweenIso(today, effectiveTo) : null;
  const notEffectiveYet = group.effectiveFrom && String(group.effectiveFrom) > today;
  const noDefaultRooms = group.rooms.filter((room) => noRoomDefaultRoomIds.includes(String(room.roomId)));

  return (
    <div
      className={['mpp-card', pendingRemoval ? 'mpp-card-pending' : '', isNew ? 'mpp-card-new' : ''].filter(Boolean).join(' ')}
      style={{ '--mpp-c': style.color }}
      data-testid="price-plan-card"
      data-group-key={group.key}
      data-state={pendingRemoval ? 'removing' : isNew ? 'new' : 'active'}
    >
      <div className="mpp-card-top">
        <Swatch style={style} />
        <span className="mpp-card-name">{group.name}</span>
        {isNew ? <span className="mpp-badge">New</span> : null}
        {pendingRemoval ? <span className="mpp-badge mpp-badge-warn" data-testid="plan-removes-on-save">Removes on Save</span> : null}
        {rate ? <span className="mpp-rate" data-testid="price-plan-rate">{formatPerHour(rate.first_hour_minor)}</span> : null}
        {canEdit && pendingRemoval ? (
          <button type="button" className="mpp-btn mpp-btn-outline mpp-btn-sm" onClick={onUndo} data-testid="plan-undo-remove">Undo</button>
        ) : null}
        {canEdit && !pendingRemoval ? (
          <button
            type="button"
            className="mpp-icon-btn"
            aria-label={`Remove ${group.name}`}
            onClick={onRemove}
            data-testid="plan-remove"
          >
            &times;
          </button>
        ) : null}
      </div>
      <ScopeChips
        roomLabels={roomLabels}
        plan={group.plan}
        style={style}
        onRemoveRoom={canEdit && isNew && !pendingRemoval ? onRemoveRoom : null}
      />
      {!pendingRemoval ? (
        <div className="mpp-cardfoot">
          {group.effectiveFrom ? (
            <span className="mpp-chip mpp-chip-date" data-testid="plan-effective-from">
              from {formatShortDate(group.effectiveFrom)}
            </span>
          ) : null}
          {effectiveTo ? <span className="mpp-chip mpp-chip-date" data-testid="plan-effective-to">until {formatShortDate(effectiveTo)}</span> : null}
          {canEdit && !isNew ? (
            <label>
              From{' '}
              <input
                type="date"
                value={dateValue || group.effectiveFrom || ''}
                onChange={(event) => onDateChange(event.target.value)}
                data-testid="plan-effective-from-input"
                aria-label={`${group.name} effective from`}
              />
            </label>
          ) : null}
          {dirtyDate ? <span className="mpp-badge mpp-badge-warn">Date changed</span> : null}
          {rate ? (
            <button
              type="button"
              className="mpp-linkbtn"
              aria-expanded={showDetail}
              onClick={() => setShowDetail((v) => !v)}
              data-testid="plan-rate-detail-toggle"
            >
              Rate detail
            </button>
          ) : null}
        </div>
      ) : null}
      {showDetail && rate && !pendingRemoval ? (
        <div className="mpp-detail" data-testid="plan-rate-detail">
          <span>First hour</span><b>{formatMinor(rate.first_hour_minor)}</b>
          <span>After {rate.threshold_minutes / 60}h</span><b>{formatPerHour(rate.long_hour_minor)}</b>
          <span>Zone</span><b>{scopeSummary(group.plan).zoned ? `Only inside ${scopeSummary(group.plan).times.join(', ')}` : 'Whole week'}</b>
        </div>
      ) : null}
      {!pendingRemoval && group.redundant ? (
        <div className="mpp-note" data-testid="plan-note-redundant">
          Never cheapest {'—'} a cheaper plan covers everywhere this applies.
        </div>
      ) : null}
      {!pendingRemoval && endsInDays !== null && endsInDays >= 0 && endsInDays <= 30 ? (
        <div className="mpp-note" data-testid="plan-note-ends">Ends in {endsInDays} day{endsInDays === 1 ? '' : 's'}.</div>
      ) : null}
      {!pendingRemoval && notEffectiveYet ? (
        <div className="mpp-note" data-testid="plan-note-future">Not effective yet (starts {formatShortDate(group.effectiveFrom)}).</div>
      ) : null}
      {!pendingRemoval && noDefaultRooms.length > 0 ? (
        <div className="mpp-note" data-testid="plan-note-no-default">
          No room default in {noDefaultRooms.map((room) => room.roomName).join(', ')} {'—'} bookings use the organisation default.
        </div>
      ) : null}
    </div>
  );
};

export default PlanCard;
